import { useTranslation } from 'react-i18next';

import { SegmentedControl, SegmentedControlItem } from '@/components/ui/SegmentedControl';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { useTheme } from '@/hooks/useTheme';
import { FlatSection } from '@/pages/settings/FlatSection';
import { api, type Locale } from '@/lib/api';
import i18nInstance from '@/i18n';

/** De talen die het scherm, de rapporten en de mails delen. Zelfde lijst als
 *  `supportedLngs` in i18n.ts, `LANGUAGES` in notifications/preferences.py en
 *  de CHECK-constraint op `users.language`. */
const LOCALES: Locale[] = ['nl', 'en', 'de', 'fr', 'pl'];

/**
 * The screen changes first and does not wait for the server: the language is a
 * browser-level preference the moment you pick it. Storing it server-side is
 * what makes the report mail arrive in the same language, so if that call
 * fails the screen keeps the new language — localStorage still has it, so the
 * only thing lost is the report mail's language, and re-picking it retries.
 */
async function changeLanguage(next: Locale): Promise<void> {
  await i18nInstance.changeLanguage(next);
  try {
    await api.me.setLanguage(next);
  } catch {
    // Deliberately swallowed — see above.
  }
}

/** Weergave — hoort bij de persoon, niet bij de organisatie. Verhuisde daarom
 *  van Instellingen naar Mijn account. */
export function AppearanceSection() {
  const { t, i18n } = useTranslation();
  const { theme, setTheme } = useTheme();
  const currentLocale = (i18n.language?.split('-')[0] ?? 'nl') as string;

  return (
    <FlatSection id="appearance" title={t('settings.appearance.heading')} note={t('settings.appearance.note')}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        <Field label={t('settings.appearance.themeLabel')}>
          <Seg
            value={theme}
            onChange={(v) => setTheme(v as 'dark' | 'light')}
            options={[
              { value: 'dark', label: t('settings.appearance.dark') },
              { value: 'light', label: t('settings.appearance.light') },
            ]}
          />
        </Field>
        <Field label={t('settings.appearance.languageLabel')}>
          <Select
            value={LOCALES.includes(currentLocale as Locale) ? currentLocale : 'en'}
            onValueChange={(v) => void changeLanguage(v as Locale)}
          >
            <SelectTrigger aria-label={t('settings.appearance.languageLabel')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LOCALES.map((lng) => (
                <SelectItem key={lng} value={lng}>
                  {t(`settings.locale.${lng}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-text-muted" style={{ fontSize: 12.5, margin: 0 }}>
            {t('settings.appearance.languageHelp')}
          </p>
        </Field>
      </div>
    </FlatSection>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7, minWidth: 0 }}>
      <label className="text-text-secondary" style={{ fontSize: 13 }}>{label}</label>
      {children}
    </div>
  );
}

/** Direct-apply segmented control. Guards against Radix's single-toggle
 *  deselect (clicking the active item would emit '' — ignore that). */
function Seg({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <SegmentedControl value={value} onValueChange={(v) => v && onChange(v)}>
      {options.map((o) => (
        <SegmentedControlItem key={o.value} value={o.value} size="sm">
          {o.label}
        </SegmentedControlItem>
      ))}
    </SegmentedControl>
  );
}
